import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useConstant } from './use-constant';
const handledEvents = ['mousedown', 'touchstart', 'keyup'];
const OnClickOutsideContext = /*#__PURE__*/createContext(null);
export const OnClickOutsideProvider = ({
  children
}) => {
  const [listening, setListening] = useState(false);
  const onClickOutsideHandlers = useConstant(() => new Set());
  const contextValue = useMemo(() => {
    function listen(handler) {
      onClickOutsideHandlers.add(handler);
      setListening(true);
      return () => {
        onClickOutsideHandlers.delete(handler);

        if (!onClickOutsideHandlers.size) {
          setListening(false);
        }
      };
    }

    return {
      listen
    };
  }, [onClickOutsideHandlers]);
  useEffect(() => {
    function runOnClickOutsideHandlers(event) {
      onClickOutsideHandlers.forEach(handler => {
        const {
          refs,
          onClickOutside,
          useEscapeKey
        } = handler;

        function runCallbackIfClickedOutside(element) {
          if (refs.some(ref => {
            var _ref$current;

            return ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.contains(element);
          })) {
            return;
          }

          onClickOutside(event);
        }

        const eventType = event.type;

        switch (eventType) {
          case 'keyup':
            {
              const {
                key,
                keyCode
              } = event;

              if (useEscapeKey && (key === 'Escape' || keyCode === 27)) {
                onClickOutside(event);
              }

              if (key !== 'Tab' && keyCode !== 9) {
                return;
              }

              runCallbackIfClickedOutside(document.activeElement);
              return;
            }

          case 'touchstart':
          case 'mousedown':
            {
              runCallbackIfClickedOutside(event.target);
              return;
            }

          default:
            {
              const unsupported = eventType;
              throw new Error(`${unsupported} is not a supported event type`);
            }
        }
      });
    }

    if (listening) {
      handledEvents.forEach(event => {
        document.addEventListener(event, runOnClickOutsideHandlers);
      });
      return () => {
        handledEvents.forEach(event => {
          document.removeEventListener(event, runOnClickOutsideHandlers);
        });
      };
    }

    return undefined;
  }, [listening, onClickOutsideHandlers]);
  return /*#__PURE__*/React.createElement(OnClickOutsideContext.Provider, {
    value: contextValue
  }, children);
};
export function useOnClickOutside(refs, onClickOutside, options) {
  const context = useContext(OnClickOutsideContext);

  if (!context) {
    throw new Error('useOnClickOutside must be called from within a OnClickOutsideProvider');
  }

  const {
    listen
  } = context;
  let useEscapeKey = false;
  let listening = false;

  if (typeof options === 'object') {
    useEscapeKey = options.useEscapeKey;
    listening = options.listening;
  } else if (typeof options === 'boolean') {
    listening = options;
  }

  useEffect(() => {
    if (listening) {
      return listen({
        refs,
        onClickOutside,
        useEscapeKey
      });
    }

    return undefined;
  }, [listen, refs, onClickOutside, options, listening, useEscapeKey]);
}